const template = document.createElement("template");

template.innerHTML = `
  <style>
    :host {
      display: block;
    }

    :host([hidden]) {
      display: none;
    }

    .dots {
      align-items: center;
      display: flex;
      gap: 8px;
      justify-content: center;
    }

    .dot {
      aspect-ratio: 1;
      background-color: var(--dot-background-color);
      border-radius: 50%;
      inline-size: 8px;
      transition: width 100ms, background-color 200ms;
    }

    .dot.active {
      background-color: var(--dot-background-color-active);
    }

    .dot.hidden {
      display: none;
    }

    .dot.small {
      inline-size: 6px;
    }

    .dot.smaller {
      inline-size: 4px;
    }
  </style>
  <div class="dots"></div>
`;

export class DotIndicator extends HTMLElement {
  #isConnected = false;
  #dotsContainer: HTMLElement | undefined;
  #dots: Element[] | undefined;
  #dotClasses: { [key: string]: string } = {
    _: "hidden",
    ".": "smaller",
    o: "small",
    O: "default",
    X: "active",
  };

  static observedAttributes = ["active-dot"];

  set numberOfDots(value) {
    this.setAttribute("number-of-dots", value.toString());
  }

  get numberOfDots() {
    return parseInt(this.getAttribute("number-of-dots") ?? "0");
  }

  set activeDot(value) {
    this.setAttribute("active-dot", value.toString());
  }

  get activeDot() {
    return parseInt(this.getAttribute("active-dot") ?? "0");
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot!.appendChild(template.content.cloneNode(true));
    this.#dotsContainer = this.shadowRoot!.querySelector(
      ".dots",
    ) as HTMLElement;
  }

  connectedCallback() {
    this.#init();
    this.#isConnected = true;
  }

  #init = () => {
    if (!this.numberOfDots || !this.activeDot) return;
    const fragment = new DocumentFragment();
    for (let i = 1; i <= this.numberOfDots; i++) {
      const span = document.createElement("span");
      fragment.appendChild(span);
    }
    this.#dotsContainer!.appendChild(fragment);
    this.#dots = Array.from(this.#dotsContainer!.children);
    this.#updateIndicatorClasses();
  };

  #updateIndicatorClasses = () => {
    if (!this.numberOfDots || !this.activeDot) return;
    const pattern = this.#getDotPattern(this.activeDot, this.numberOfDots);
    if (pattern && pattern.length === this.#dots!.length) {
      this.#dots!.forEach((element, index) => {
        element.className = "dot";
        const key = pattern[index];
        if (key && this.#dotClasses[key]) {
          element.classList.add(this.#dotClasses[key]);
        }
      });
    }
  };

  #replaceString = (source: string, index: number, replacement: string) => {
    if (index < 0) index = source.length + index + 1;
    const pre = source.substring(0, index - 1);
    const post = source.substring(index + replacement.length - 1);
    return pre + replacement + post;
  };

  // index and max are 1-based
  #getDotPattern = (index: number, max: number) => {
    if (max < 7) {
      // called with e.g. (4, 6): 123'56 => "OOO" + "X" + "OO"
      const pre = index - 1;
      const post = max - index;
      return "O".repeat(pre) + "X" + "O".repeat(post);
    }

    const pattern = "_".repeat(max);
    if (index === 1) return this.#replaceString(pattern, 1, "XOo.");
    if (index === 2) return this.#replaceString(pattern, 1, "OXo.");
    if (index === 3) return this.#replaceString(pattern, 1, "oOXo.");
    if (index === 4) return this.#replaceString(pattern, 1, "oOOXo.");
    if (index > 4 && max - index > 1)
      return this.#replaceString(pattern, index - 4, ".oOOXo.");
    if (max - index === 1) return this.#replaceString(pattern, -6, ".oOOXo");
    if (max - index === 0) return this.#replaceString(pattern, -5, ".oOOX");
  };

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (!this.#isConnected) return;
    if (name === "active-dot" && newValue && newValue !== oldValue) {
      this.#updateIndicatorClasses();
    }
  }
}

"customElements" in window &&
  customElements.get("ws-dot-indicator") === undefined &&
  customElements.define("ws-dot-indicator", DotIndicator);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-dot-indicator": DotIndicator;
  }
}
